import type { FC } from 'react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox as MuiCheckbox,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LayersIcon from '@mui/icons-material/Layers';
import type { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, GridOverlay } from '@mui/x-data-grid';
import type {
  CreativeDnDMovePayload,
  CreativeDnDZone,
  HypothesisCreativeEntry,
  HypothesisSection,
} from '../../types/hypothesis';
import type { HypothesisPack } from '../../hooks';
import { AddPackTagButton } from 'components/common';
import CreativeDragHandle from './CreativeDragHandle';
import { ALL_COLUMN_DEFS } from './creativeColumns';
import { buildMovePayload, parseHypothesisCreativeDrag, resolveDropIndex, } from './hypothesisCreativeDnD';

const EMPTY_PACKS: HypothesisPack[] = [];

const packDisplayTag = (tag: string): string => (tag.trim() ? tag : 'Pack');

interface HypothesisGroupCardProps {
  hypothesis: string;
  creatives: HypothesisCreativeEntry[];
  packs?: HypothesisPack[];
  section: HypothesisSection | null;
  showSectionSelector: boolean;
  selectedIds: string[];
  columns: GridColDef[];
  dragEnabled?: boolean;
  canEditPackTag?: boolean;
  /** Live campaign tags from form state, indexed by pack index. */
  packTagsByIndex?: string[];
  /** Renders pack grids only — used for the mixed pack section at the top. */
  hideHypothesisHeader?: boolean;
  onSelectionChange: (hypothesis: string, ids: string[]) => void;
  onPackSelectionChange?: (packIndex: number, packCreativeIds: string[]) => void;
  onActivatePack?: (packIndex: number) => void;
  onSectionChange?: (hypothesis: string, section: HypothesisSection) => void;
  onCreativeMove?: (hypothesis: string, move: CreativeDnDMovePayload) => void;
}

const dataGridSx = {
  border: 'none',
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-columnHeaders': {
    borderTop: 1,
    borderColor: 'divider',
    bgcolor: 'transparent',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'text.secondary',
  },
  '& .hypothesis-row-dragging': {
    opacity: 0.35,
  },
} as const;

const DRAG_HANDLE_FIELD = '__dragHandle';
const ROW_SELECT_FIELD = '__select__';

const HypothesisCreativesEmptyOverlay = ({
  isDragOver,
}: {
  isDragOver: boolean;
}) => (
  <GridOverlay
    sx={{
      backgroundColor: 'transparent',
      alignItems: 'stretch',
      justifyContent: 'stretch',
    }}
  >
    <Box
      sx={{
        position: 'sticky',
        top: '50%',
        transform: 'translateY(-50%)',
        alignSelf: 'center',
        width: 'calc(100% - 32px)',
        mx: 2,
        minHeight: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed',
        borderColor: isDragOver ? 'primary.main' : 'divider',
        borderRadius: 1,
        transition: 'border-color 0.15s ease-in-out',
      }}
    >
      <Typography
        variant="body2"
        color={isDragOver ? 'primary.main' : 'text.secondary'}
      >
        Drag creatives here
      </Typography>
    </Box>
  </GridOverlay>
);

const buildRowSelectionColumn = (
  rows: HypothesisCreativeEntry[],
  rowSelectionModel: string[],
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void,
): GridColDef => ({
  field: ROW_SELECT_FIELD,
  headerName: '',
  width: 52,
  minWidth: 52,
  maxWidth: 52,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  resizable: false,
  renderHeader: () => {
    const rowIds = rows.map((row) => row.id);
    const allSelected =
      rowIds.length > 0 && rowIds.every((id) => rowSelectionModel.includes(id));
    const someSelected =
      !allSelected && rowIds.some((id) => rowSelectionModel.includes(id));

    return (
      <MuiCheckbox
        size="small"
        checked={allSelected}
        indeterminate={someSelected}
        onChange={() => {
          onRowSelectionModelChange(allSelected ? [] : rowIds);
        }}
        onClick={(event) => { event.stopPropagation(); }}
      />
    );
  },
  renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => {
    const id = String(params.id);
    const checked = rowSelectionModel.includes(id);

    return (
      <MuiCheckbox
        size="small"
        checked={checked}
        onChange={(event) => {
          event.stopPropagation();
          const next = event.target.checked
            ? [...rowSelectionModel, id]
            : rowSelectionModel.filter((selectedId) => selectedId !== id);
          onRowSelectionModelChange(next);
        }}
        onClick={(event) => { event.stopPropagation(); }}
      />
    );
  },
});

interface HypothesisCreativesGridProps {
  rows: HypothesisCreativeEntry[];
  columns: GridColDef[];
  zone: CreativeDnDZone;
  dragEnabled: boolean;
  rowSelectionModel: string[];
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  onCreativeMove?: (move: CreativeDnDMovePayload) => void;
  draggingCreativeId?: string | null;
  onCreativeDragStart?: (creativeId: string) => void;
  onCreativeDragEnd?: () => void;
  hideColumnHeaders?: boolean;
  hideTopBorder?: boolean;
  showEmptyDropTarget?: boolean;
}

const HypothesisCreativesGrid: FC<HypothesisCreativesGridProps> = ({
  rows,
  columns,
  zone,
  dragEnabled,
  rowSelectionModel,
  onRowSelectionModelChange,
  onCreativeMove,
  draggingCreativeId = null,
  onCreativeDragStart,
  onCreativeDragEnd,
  hideColumnHeaders = false,
  hideTopBorder = false,
  showEmptyDropTarget = false,
}) => {
  const dropContainerRef = useRef<HTMLDivElement>(null);
  const [isDragOverDropTarget, setIsDragOverDropTarget] = useState(false);

  const emptyOverlaySlot = useCallback(
    () => <HypothesisCreativesEmptyOverlay isDragOver={isDragOverDropTarget} />,
    [isDragOverDropTarget],
  );

  const gridColumns = useMemo(() => {
    const dataColumns = columns.filter((col) => col.field !== DRAG_HANDLE_FIELD);

    if (!dragEnabled) return columns;

    const dragColumn: GridColDef = {
      ...ALL_COLUMN_DEFS.dragHandle,
      renderCell: (params: GridRenderCellParams<HypothesisCreativeEntry>) => (
        <CreativeDragHandle
          creativeId={params.row.id}
          rowIndex={params.api.getRowIndexRelativeToVisibleRows(params.id)}
          zone={zone}
          onDragStart={onCreativeDragStart}
          onDragEnd={onCreativeDragEnd}
        />
      ),
    };

    const selectionColumn = buildRowSelectionColumn(
      rows,
      rowSelectionModel,
      onRowSelectionModelChange,
    );

    return [selectionColumn, dragColumn, ...dataColumns];
  }, [
    columns,
    dragEnabled,
    zone,
    rows,
    rowSelectionModel,
    onRowSelectionModelChange,
    onCreativeDragStart,
    onCreativeDragEnd,
  ]);

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!dragEnabled) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (showEmptyDropTarget && rows.length === 0) {
        setIsDragOverDropTarget(true);
      }
    },
    [dragEnabled, showEmptyDropTarget, rows.length],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const related = event.relatedTarget;
      if (
        related instanceof Node &&
        dropContainerRef.current?.contains(related)
      ) {
        return;
      }
      setIsDragOverDropTarget(false);
    },
    [],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!dragEnabled || !onCreativeMove) return;
      event.preventDefault();
      setIsDragOverDropTarget(false);
      const dragData = parseHypothesisCreativeDrag(event.dataTransfer);
      if (!dragData) return;

      const index = resolveDropIndex(event.clientY, dropContainerRef.current);
      const to =
        zone.kind === 'pack'
          ? { kind: 'pack' as const, packIndex: zone.packIndex, index }
          : { kind: 'unpacked' as const, index };

      onCreativeMove(buildMovePayload(dragData, to));
    },
    [dragEnabled, onCreativeMove, zone],
  );

  const grid = (
    <DataGrid
      rows={rows}
      columns={gridColumns}
      checkboxSelection={!dragEnabled}
      rowSelection={!dragEnabled}
      {...(dragEnabled
        ? {}
        : {
            rowSelectionModel,
            onRowSelectionModelChange,
            keepNonExistentRowsSelected: true,
          })}
      hideFooterPagination
      hideFooter
      disableColumnMenu
      disableVirtualization={dragEnabled}
      rowHeight={60}
      getRowId={(row: HypothesisCreativeEntry) => row.id}
      getRowClassName={(params) =>
        params.id === draggingCreativeId ? 'hypothesis-row-dragging' : ''
      }
      slots={
        showEmptyDropTarget
          ? { noRowsOverlay: emptyOverlaySlot }
          : undefined
      }
      sx={
        hideColumnHeaders
          ? {
              ...dataGridSx,
              '& .MuiDataGrid-columnHeaders': {
                display: 'none',
                ...(hideTopBorder ? { borderTop: 'none' } : {}),
              },
            }
          : hideTopBorder
            ? {
                ...dataGridSx,
                '& .MuiDataGrid-columnHeaders': { borderTop: 'none' },
              }
            : dataGridSx
      }
    />
  );

  if (!dragEnabled) {
    return grid;
  }

  return (
    <Box
      ref={dropContainerRef}
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDragEnd={() => { setIsDragOverDropTarget(false); }}
      onDrop={handleDrop}
      sx={
        showEmptyDropTarget
          ? { minHeight: 100, borderBottom: 1, borderColor: 'divider' }
          : undefined
      }
    >
      {grid}
    </Box>
  );
};

const HypothesisGroupCardImpl: FC<HypothesisGroupCardProps> = ({
  hypothesis,
  creatives,
  packs = EMPTY_PACKS,
  section,
  showSectionSelector,
  selectedIds,
  columns,
  dragEnabled = false,
  canEditPackTag = false,
  packTagsByIndex,
  hideHypothesisHeader = false,
  onSelectionChange,
  onPackSelectionChange,
  onActivatePack,
  onSectionChange,
  onCreativeMove,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [draggingCreativeId, setDraggingCreativeId] = useState<string | null>(null);

  const handleCreativeDragStart = useCallback((creativeId: string) => {
    setDraggingCreativeId(creativeId);
  }, []);

  const handleCreativeDragEnd = useCallback(() => {
    setDraggingCreativeId(null);
  }, []);

  const groupCreativeIds = useMemo(
    () => creatives.map((c) => c.id),
    [creatives],
  );

  const totalCount =
    creatives.length +
    packs.reduce((sum, pack) => sum + pack.creatives.length, 0);
  const allSelected =
    selectedIds.length === creatives.length && creatives.length > 0;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSelectAll = useCallback(() => {
    onSelectionChange(hypothesis, allSelected ? [] : groupCreativeIds);
  }, [allSelected, groupCreativeIds, hypothesis, onSelectionChange]);

  const handleRowSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      onSelectionChange(hypothesis, model.map(String));
    },
    [hypothesis, onSelectionChange],
  );

  const handlePackRowSelectionChange = useCallback(
    (packIndex: number, model: GridRowSelectionModel) => {
      if (!onPackSelectionChange) return;
      onPackSelectionChange(packIndex, model.map(String));
    },
    [onPackSelectionChange],
  );

  const handleCreativeMove = useCallback(
    (move: CreativeDnDMovePayload) => {
      onCreativeMove?.(hypothesis, move);
    },
    [hypothesis, onCreativeMove],
  );

  const hasPacks = packs.length > 0;
  const hasUnpacked = creatives.length > 0;
  const effectiveDragEnabled = dragEnabled && !hideHypothesisHeader;
  const showUnpackedSection = !hideHypothesisHeader && hasUnpacked;

  const packLabels = useMemo(() => {
    const packsWithTags = packs.map((p) => ({
      ...p,
      tag: packDisplayTag(packTagsByIndex?.[p.packIndex] ?? p.tag),
    }));
    const tagOccurrences = new Map<string, number>();
    for (const p of packsWithTags) {
      tagOccurrences.set(p.tag, (tagOccurrences.get(p.tag) ?? 0) + 1);
    }
    const ordinalByTag = new Map<string, number>();
    return packsWithTags.map((p) => {
      const sameTagTotal = tagOccurrences.get(p.tag) ?? 1;
      if (sameTagTotal <= 1) {
        return `Pack: ${p.tag} (${p.creatives.length})`;
      }
      const nextOrdinal = (ordinalByTag.get(p.tag) ?? 0) + 1;
      ordinalByTag.set(p.tag, nextOrdinal);
      return `Pack: ${p.tag} (${nextOrdinal}) (${p.creatives.length})`;
    });
  }, [packs, packTagsByIndex]);

  const packSection = (
    <>
      {packs.map((pack, packIdx) => {
        const packIds = pack.creatives.map((c) => c.id);
        return (
          <Box key={`pack-${String(pack.packIndex)}`} sx={{ mt: hideHypothesisHeader && packIdx === 0 ? 0 : 0.5 }}>
            <Box
              sx={{
                px: 2,
                py: 2,
                borderTop: hideHypothesisHeader && packIdx === 0 ? 0 : 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <LayersIcon
                  fontSize="medium"
                  sx={{ color: 'text.primary' }}
                />
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={'text.primary'}
                  noWrap
                >
                  {packLabels[packIdx]}
                </Typography>
              </Box>
              {canEditPackTag && (
                <Box sx={{ flexShrink: 0, flexDirection: 'row', display: 'flex', gap: 2 }}>
                  {canEditPackTag && onActivatePack && (
                    <Button
                      variant="outlined"
                      size={'small'}
                      color={'primary'}
                      onClick={() => {
                        onActivatePack(pack.packIndex);
                      }}
                      sx={{ flexShrink: 0, textTransform: 'initial' }}
                    >
                      + Add more creatives
                    </Button>
                  )}
                  <AddPackTagButton packIndex={pack.packIndex} requireTag />
                </Box>
              )}
            </Box>
            <HypothesisCreativesGrid
              rows={pack.creatives}
              columns={columns}
              zone={{ kind: 'pack', packIndex: pack.packIndex }}
              dragEnabled={effectiveDragEnabled}
              rowSelectionModel={packIds}
              onRowSelectionModelChange={(model) => {
                handlePackRowSelectionChange(pack.packIndex, model);
              }}
              onCreativeMove={handleCreativeMove}
              draggingCreativeId={draggingCreativeId}
              onCreativeDragStart={handleCreativeDragStart}
              onCreativeDragEnd={handleCreativeDragEnd}
              hideColumnHeaders={packIdx !== 0}
              hideTopBorder={packIdx === 0}
              showEmptyDropTarget={effectiveDragEnabled && pack.creatives.length === 0}
            />
          </Box>
        );
      })}
      {hasPacks && showUnpackedSection && (
        <Box
          sx={{
            px: 1.5,
            pt: 2,
            pb: hasUnpacked ? 2 : 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="body2"
            fontWeight={500}
            color="text.primary"
          >
            Creatives:
          </Typography>
        </Box>
      )}
      {showUnpackedSection && (
        <HypothesisCreativesGrid
          rows={creatives}
          columns={columns}
          zone={{ kind: 'unpacked' }}
          dragEnabled={effectiveDragEnabled}
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={handleRowSelectionChange}
          onCreativeMove={handleCreativeMove}
          draggingCreativeId={draggingCreativeId}
          onCreativeDragStart={handleCreativeDragStart}
          onCreativeDragEnd={handleCreativeDragEnd}
          hideColumnHeaders={hasPacks}
          showEmptyDropTarget={effectiveDragEnabled && hasPacks && !hasUnpacked}
        />
      )}
    </>
  );

  if (hideHypothesisHeader) {
    return (
      <Box
        sx={{
          mb: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {packSection}
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: .75,
          pr: 1.5,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MuiCheckbox
            size="medium"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            onClick={(e) => { e.stopPropagation(); }}
          />
          <IconButton
            size="small"
            color='primary'
            onClick={() => { setExpanded((prev) => !prev); }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ cursor: 'pointer', color: 'primary.main' }}
            onClick={() => { setExpanded((prev) => !prev); }}
          >
            {hypothesis}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            (Creatives: {totalCount})
          </Typography>
        </Box>
        {showSectionSelector && onSectionChange && (
          <FormControl size="small" variant="outlined">
            <Select
              value={section ?? 'upcoming'}
              onChange={(e) => {
                onSectionChange(hypothesis, e.target.value as HypothesisSection);
              }}
              size="small"
              sx={{ height: 32, minWidth: 120, fontSize: '0.875rem' }}
            >
              <MenuItem value="this_week">This week</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
      <Collapse in={expanded} unmountOnExit>
        {packSection}
      </Collapse>
    </Card>
  );
};

const HypothesisGroupCard = memo(HypothesisGroupCardImpl);

export default HypothesisGroupCard;
